import React, { useState, useEffect } from 'react';
import { Breadcrumb, Button, Input, message, Modal } from 'antd';
import { HomeIcon, DocumentChartBarIcon } from "@heroicons/react/16/solid";
import HolidayTable from './holidayTable';
import DatePickerComponent from '../DatePicker/index';
import { postRequest, getRequest } from '../../utils/APICall';
import moment from 'moment';
import { useSelector } from 'react-redux';

const { TextArea } = Input;

const Holiday = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [description, setDescription] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitModalVisible, setIsSubmitModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const { users } = useSelector((state) => state.user || {})

  const fetchHolidays = async () => {
    try {
      const res = await getRequest('getHoliday');
      if (res.success) {
        setHolidays(res.data);
      } else {
        setHolidays([])
        // message.error(res.message || "Failed to fetch holidays");
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      message.error("Something went wrong while fetching holidays.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const validateInputs = () => {
    if (!fromDate) {
      message.error("Please select a From Date");
      return false;
    }
    if (!toDate) {
      message.error("Please select a To Date");
      return false;
    }
    if (!description.trim()) {
      message.error("Please enter a Description");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        vid: isEditing ? editId : 0,
        vfromdate: fromDate,
        vtodate: toDate,
        vDescription: description.trim(),
        vstatus: 1,
        vcreatedBy : `${users?.FIRSTNAME} ${users?.LASTNAME}| ${moment(Date.now()).format("DD-MM-YYYY HH:mm")}`,
      };
      // console.log(payload)
      const res = await postRequest('addHoliday', payload);
      if (res.success) {
        message.success(isEditing ? "Holiday updated successfully" : "Holiday added successfully");
        setFromDate(null);
        setToDate(null);
        setDescription('');
        setIsEditing(false);
        setEditId(null);
        fetchHolidays();
      } else {
        message.error(res.message || "Failed to add/update holiday");
      }
    } catch (error) {
      console.error("Error submitting holiday:", error);
      message.error("Something went wrong. Please try again.");
    }
  };

  const showSubmitModal = () => {
    if (validateInputs()) {
      setIsSubmitModalVisible(true);
    }
  };

  const handleSubmitConfirm = () => {
    setIsSubmitModalVisible(false);
    handleSubmit();
  };

  const handleSubmitCancel = () => {
    setIsSubmitModalVisible(false);
  };

  const handleDelete = async (id) => {
    try {
      const payload = {
        vid: id,
        vfromdate: null,
        vtodate: null,
        vDescription: null,
        vcreatedBy : `${users?.FIRSTNAME} ${users?.LASTNAME}| ${moment(Date.now()).format("DD-MM-YYYY HH:mm")}`,
        vstatus: 0
      };

      // console.log("id", id)
      const res = await postRequest('addHoliday', payload);
      if (res.success) {
        message.success("Holiday soft deleted successfully");
        fetchHolidays();
      } else {
        message.error(res.message || "Failed to delete holiday");
      }
    } catch (error) {
      console.error("Error deleting holiday:", error);
      message.error("Something went wrong. Please try again.");
    }
  };

  const showDeleteModal = (id) => {
    setDeleteId(id);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteConfirm = () => {
    setIsDeleteModalVisible(false);
    handleDelete(deleteId);
    setDeleteId(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalVisible(false);
    setDeleteId(null);
  };



  const handleEdit = ({ ID, fromDate, toDate, description }) => {
    setFromDate(moment(fromDate).format('DD-MMMM-YYYY'));
    setToDate(moment(toDate).format('DD-MMMM-YYYY'));
    setDescription(description);
    setIsEditing(true);
    setEditId(ID);
  };

  const breadcrumbItems = [
    {
      href: '/',
      title: (
        <span className='flex sm:gap-2 items-center'>
          <HomeIcon className="h-6 w-6 text-gray-500" />
          <span className='text-gray-400 sm:text-lg'>Home</span>
        </span>
      ),
    },
    {
      href: '/holiday',
      title: (
        <span className='flex sm:gap-2 items-center'>
          <DocumentChartBarIcon className="h-6 w-6 text-gray-300" />
          <span className='text-gray-400 sm:text-lg'>Holiday Schedule</span>
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <span className="flex flex-wrap py-6 px-2 sm:p-10 gap-4 justify-between items-start md:items-center w-full">
        <span className="flex flex-col gap-2 text-2xl md:text-4xl font-black w-full md:w-auto dark:text-white">
          Holiday Schedule
          <Breadcrumb items={breadcrumbItems} />
        </span>
      </span>

      {/* Form */}
      <span className="flex flex-wrap gap-6 items-center px-4 pt-2 pb-4 ">
        <span className="flex flex-col w-full sm:w-[300px]">
          <label className="font-semibold mb-1 dark:text-white">
            From Date <span className="text-red-500">*</span>
          </label>
          <DatePickerComponent
            date={fromDate}
            onChange={(date, dateString) => setFromDate(dateString)}
            customStyle="!w-full h-10"
          />
        </span>

        <span className="flex flex-col w-full sm:w-[300px]">
          <label className="font-semibold mb-1 dark:text-white">
            To Date <span className="text-red-500">*</span>
          </label>
          <DatePickerComponent
            date={toDate}
            onChange={(date, dateString) => setToDate(dateString)}
            customStyle="!w-full h-10"
          />
        </span>

        <span className="flex flex-col w-full sm:w-[300px]">
          <label className="font-semibold mb-1 dark:text-white">
            Description <span className="text-red-500">*</span>
          </label>
          <TextArea
            placeholder="Description"
            rows={2}
            className="!w-full !min-w-0"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </span>

        <span className="flex items-center gap-4 h-full">
          <span className="flex flex-col h-full">
            <br />
            <Button
              color="primary"
              size="large"
              className='text-lg font-bold text-gray-100 p-4 px-12'
              variant="solid"
              onClick={showSubmitModal}
            >
              {isEditing ? 'Update' : 'Submit'}
            </Button>
          </span>
          
        </span>
      </span>

      {/* Submit Confirmation Modal */}
      <Modal
        title="Confirm Submission"
        open={isSubmitModalVisible}
        onOk={handleSubmitConfirm}
        onCancel={handleSubmitCancel}
        okText="Yes"
        cancelText="Cancel"
      >
        <p>Are you sure you want to {isEditing ? 'update' : 'submit'} this holiday?</p>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={isDeleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p className=''>Are you sure you want to delete this holiday? This action will mark the holiday as inactive.</p>
      </Modal>

      {/* Table */}
      <HolidayTable holidays={holidays} loading={loading} onEdit={handleEdit} onDelete={showDeleteModal} />
    </div>
  );
};

export default Holiday;